import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { projectsAPI, paymentAPI, configAPI } from '../services/api';
import PaymentButton from '../components/PaymentButton';
import './PlanViewer.css';

const PlanViewer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [plan, setPlan] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paymentVerifying, setPaymentVerifying] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);

  useEffect(() => {
    fetchData();
    fetchConfig();
    checkPaymentSuccess();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [planRes, projectRes] = await Promise.all([
        projectsAPI.getPlan(projectId),
        projectsAPI.getById(projectId)
      ]);

      setPlan(planRes.data.data);
      setProject(projectRes.data.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load migration plan');
      setLoading(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await configAPI.getConfig();
      setIsTestMode(response.data.data.paymentTestMode);

      if (response.data.data.paymentTestMode) {
        console.log('⚠️  Payment Test Mode is ENABLED - Downloads available without payment');
      }
    } catch (err) {
      // Silently fail - test mode defaults to false
      console.error('Failed to fetch config:', err);
    }
  };

  const checkPaymentSuccess = async () => {
    // Check if we're returning from Stripe checkout
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (sessionId) {
      setPaymentVerifying(true);
      try {
        const response = await paymentAPI.verifyPayment(sessionId);
        if (response.data.status === 'completed') {
          setPaymentSuccess(true);
          // Refresh project data to get updated payment status
          const projectRes = await projectsAPI.getById(projectId);
          setProject(projectRes.data.data);
          
          // Clear URL parameter
          window.history.replaceState({}, '', `/projects/${projectId}/plan`);
        }
      } catch (err) {
        console.error('Payment verification error:', err);
      }
      setPaymentVerifying(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');

    try {
      const response = await projectsAPI.downloadPlan(projectId);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${plan.projectName.replace(/[^a-z0-9]/gi, '_')}-migration-plan.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required
        setShowPaymentModal(true);
      } else {
        setError(err.response?.data?.message || 'Failed to download plan');
      }
    }

    setDownloading(false);
  };

  const handleTemplateDownload = async () => {
    setDownloading(true);
    setError('');

    try {
      const response = await paymentAPI.downloadTemplates(projectId);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'DDMF_Project_Template.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status === 402) {
        // Payment required
        setShowPaymentModal(true);
      } else {
        setError(err.response?.data?.message || 'Failed to download templates');
      }
    }

    setDownloading(false);
  };

  const calculateAnsweredQuestions = (section) => {
    return section.questions.filter((q) => q.answered).length;
  };

  const hasValidAccess = () => {
    // If test mode is enabled, bypass payment check
    if (isTestMode) {
      return true;
    }

    if (!project) return false;

    const isSample = project.isSample;
    const purchaseType = isSample ? 'templates' : 'plan';

    if (project.purchaseType !== purchaseType) return false;
    if (!project.accessExpiryDate) return false;

    return new Date(project.accessExpiryDate) > new Date();
  };

  const getAccessExpiryText = () => {
    if (!project || !project.accessExpiryDate) return null;
    
    const expiryDate = new Date(project.accessExpiryDate);
    const now = new Date();
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) {
      return { text: 'Access expired', className: 'expired' };
    } else if (daysLeft < 30) {
      return { text: `Expires in ${daysLeft} days`, className: 'expiring-soon' };
    } else {
      return { text: `Expires: ${expiryDate.toLocaleDateString()}`, className: 'active' };
    }
  };

  if (loading) {
    return (
      <div className="plan-viewer-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !plan || !project) {
    return (
      <div className="plan-viewer-container">
        <div className="container py-4">
          <div className="alert alert-error">
            {error || 'Failed to load plan'}
          </div>
          <button
            className="btn btn-outline"
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const isSample = project.isSample;
  const canDownload = hasValidAccess();
  const downloadPrice = isSample ? 499 : 199;
  const purchaseType = isSample ? 'templates' : 'plan';
  const accessInfo = getAccessExpiryText();

  return (
    <div className="plan-viewer-container">
      {/* Payment Success Banner */}
      {paymentVerifying && (
        <div className="alert alert-info">
          Verifying payment...
        </div>
      )}

      {paymentSuccess && (
        <div className="alert alert-success">
          ✅ Payment successful! You now have 1-year access to download your {isSample ? 'templates' : 'plan'}.
        </div>
      )}

      {/* Header */}
      <div className="plan-header">
        <div className="container">
          <div className="plan-header-content">
            <div>
              <h1>Data Migration Plan</h1>
              {isSample && (
                <span className="sample-badge">📝 Sample Project</span>
              )}
              <h2 className="project-name">{plan.projectName}</h2>
              {plan.projectDescription && (
                <p className="project-description">{plan.projectDescription}</p>
              )}
            </div>
            <div className="plan-header-actions">
              {!isSample && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => navigate(`/projects/${projectId}/discovery`)}
                >
                  Edit Answers
                </button>
              )}
              {canDownload ? (
                <>
                  <button
                    className="btn btn-success btn-lg download-btn"
                    onClick={isSample ? handleTemplateDownload : handleDownload}
                    disabled={downloading}
                  >
                    {downloading ? 'Downloading...' : `📥 Download ${isSample ? 'Templates' : 'Plan'}`}
                  </button>
                  {accessInfo && (
                    <span className={`access-info ${accessInfo.className}`}>
                      {accessInfo.text}
                    </span>
                  )}
                </>
              ) : (
                <button
                  className="btn btn-primary btn-lg download-btn"
                  onClick={() => setShowPaymentModal(true)}
                >
                  📥 Download {isSample ? 'Templates' : 'Plan'} (${downloadPrice})
                </button>
              )}
            </div>
          </div>

          <div className="plan-meta">
            <div className="plan-meta-item">
              <strong>Generated:</strong>{' '}
              {new Date(plan.generatedAt).toLocaleString()}
            </div>
            <div className="plan-meta-item">
              <strong>Completion:</strong> {plan.completionPercentage}%
            </div>
          </div>
        </div>
      </div>

      {/* Plan Content */}
      <div className="container py-4">
        <div className="plan-content">
          {/* Introduction */}
          <div className="plan-section">
            <h2 className="section-title">Executive Summary</h2>
            <div className="section-content">
              <p>
                This data migration plan was created using the Data Migration
                Framework methodology by Krupesh Desai. It provides a comprehensive
                roadmap for your legacy system decommissioning project.
              </p>
              {isSample && (
                <div className="sample-notice">
                  <strong>📝 This is a Sample Project</strong>
                  <p>
                    This example demonstrates a complete data migration plan for a
                    healthcare system migration. All answers are pre-filled to show
                    best practices. Create your own project to develop a custom plan.
                  </p>
                </div>
              )}
              <div className="completion-notice">
                <strong>Plan Completion: {plan.completionPercentage}%</strong>
                {plan.completionPercentage < 100 && (
                  <p>
                    Some questions are still unanswered. Consider completing all
                    quesitons in the first three components for a comprehensive project discovery and an actionable data migration plan.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sections */}
          {plan.sections.map((section, index) => {
            const answeredCount = calculateAnsweredQuestions(section);
            const totalCount = section.questions.length;

            return (
              <div key={section.id} className="plan-section">
                <div className="section-header">
                  <div>
                    <h2 className="section-title">
                      {index + 1}. {section.name}
                    </h2>
                    <p className="section-description">{section.description}</p>
                  </div>
                  <div className="section-progress">
                    <span className="section-progress-text">
                      {answeredCount} / {totalCount} answered
                    </span>
                  </div>
                </div>

                <div className="section-content">
                  {section.questions.map((question, qIndex) => (
                    <div
                      key={question.id}
                      className={`question-item ${
                        !question.answered ? 'unanswered' : ''
                      }`}
                    >
                      <div className="question-text">
                        <span className="question-number">
                          {index + 1}.{qIndex + 1}
                        </span>
                        {question.question}
                      </div>
                      <div className="answer-text">
                        {question.answered ? (
                          question.answer
                        ) : (
                          <span className="no-answer">Not answered yet</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Download CTA */}
          {!canDownload && (
            <div className="plan-cta-section">
              <div className="plan-cta-content">
                <h3>Ready to Download Your {isSample ? 'Templates' : 'Migration Plan'}?</h3>
                <p>
                  {isSample ? (
                    <>
                      Get the complete DDMF Project Template bundle including the Data
                      Migration Plan, Policy documents, Risk Register, and Cutover
                      Checklist for ${downloadPrice}.
                    </>
                  ) : (
                    <>
                      Download your complete Data Migration Plan as a professionally
                      formatted Word document for ${downloadPrice}. This includes all your answers
                      organized by the framework components.
                    </>
                  )}
                </p>
                <PaymentButton
                  projectId={projectId}
                  purchaseType={purchaseType}
                  amount={downloadPrice}
                  buttonText={`Pay $${downloadPrice} & Download ${isSample ? 'Templates' : 'Plan'}`}
                />
                <p className="download-note">
                  1-year access • Unlimited downloads • Professional format
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Download {isSample ? 'Template Bundle' : 'Migration Plan'}</h2>
              <button
                className="modal-close"
                onClick={() => setShowPaymentModal(false)}
              >
                &times;
              </button>
            </div>

            <div className="payment-modal-body">
              <div className="payment-details">
                <h3>What You'll Get:</h3>
                {isSample ? (
                  <ul>
                    <li>✓ Complete Data Migration Plan template</li>
                    <li>✓ Data Migration Policy template</li>
                    <li>✓ Risk Register template</li>
                    <li>✓ Cutover Checklist template</li>
                    <li>✓ All templates in professional Word format</li>
                    <li>✓ 1-year unlimited download access</li>
                  </ul>
                ) : (
                  <ul>
                    <li>✓ Complete Data Migration Plan in Word format</li>
                    <li>✓ All 10 framework sections with your answers</li>
                    <li>✓ Professional formatting and structure</li>
                    <li>✓ Ready to share with stakeholders</li>
                    <li>✓ 1-year unlimited download access</li>
                  </ul>
                )}

                <div className="price-section">
                  <div className="price">${downloadPrice}</div>
                  <p>One-time payment • 1-year access • No subscription</p>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-outline"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Maybe Later
                </button>
                <PaymentButton
                  projectId={projectId}
                  purchaseType={purchaseType}
                  amount={downloadPrice}
                  buttonText={`Pay $${downloadPrice} & Download`}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanViewer;
